import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import {
  Box,
  Typography,
  Button,
  TextField,
  CircularProgress,
  Paper,
} from "@mui/material";
import {
  LocationOn,
  CheckCircle,
  Cancel,
  Refresh,
} from "@mui/icons-material";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { MarkAttendance } from "../../../Services/Services";

function MarkAttendanceComponent() {
  const [searchParams] = useSearchParams();
  const token = searchParams.get("token");

  const [step, setStep] = useState("init");
  const [employeeId, setEmployeeId] = useState("");
  const [location, setLocation] = useState({ latitude: null, longitude: null });
  const [locationError, setLocationError] = useState("");
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!token) {
      setError("Invalid QR Code. Please scan the office QR again.");
      setStep("error");
      return;
    }
    getLocation();
  }, [token]);

  const getLocation = () => {
    setLocationError("");
    setStep("location");

    if (!navigator.geolocation) {
      setLocationError("Geolocation not supported by your browser");
      return;
    }

    navigator.geolocation.getCurrentPosition(
      (position) => {
        setLocation({
          latitude: position.coords.latitude,
          longitude: position.coords.longitude,
        });
        setStep("form");
      },
      (err) => {
        let msg = "Location access denied";
        if (err.code === 1) msg = "❌ Please allow location access to mark attendance";
        if (err.code === 2) msg = "❌ Location unavailable";
        if (err.code === 3) msg = "❌ Location request timeout";
        setLocationError(msg);
      },
      { enableHighAccuracy: true, timeout: 15000, maximumAge: 0 }
    );
  };

  const submitAttendance = async () => {
    if (!employeeId.trim()) {
      toast.error("Please enter your Employee ID");
      return;
    }

    setLoading(true);
    setError("");

    try {
      const data = await MarkAttendance({
        token: token,
        employee_id: employeeId.trim().toUpperCase(),
        latitude: location.latitude,
        longitude: location.longitude,
      });
      setResult(data);
      setStep("success");
      toast.success("Attendance marked successfully!");
    } catch (err) {
      const errMsg = err.response?.data?.error || "Failed to mark attendance";
      setError(errMsg);
      setStep("error");
      toast.error(errMsg);
    } finally {
      setLoading(false);
    }
  };

  const reset = () => {
    setStep("form");
    setResult(null);
    setError("");
    setEmployeeId("");
  };

  return (
    <>
      <ToastContainer />
      <Box
        sx={{
          minHeight: "100vh",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          bgcolor: "#f8f9fa",
          p: 2,
        }}
      >
        <Paper
          sx={{
            maxWidth: 400,
            width: "100%",
            p: 3,
            borderRadius: "16px",
            boxShadow: "0 8px 32px rgba(0,0,0,0.1)",
          }}
        >
          {/* Header */}
          <Box sx={{ textAlign: "center", mb: 3 }}>
            <Typography sx={{ fontSize: "32px" }}>🏢</Typography>
            <Typography sx={{ fontSize: "22px", fontWeight: 700, color: "#1e293b" }}>
              Mark Attendance
            </Typography>
            <Typography sx={{ fontSize: "13px", color: "#64748b" }}>
              Office Attendance System
            </Typography>
          </Box>

          {/* STEP: Getting Location */}
          {step === "location" && !locationError && (
            <Box sx={{ textAlign: "center", py: 4 }}>
              <CircularProgress sx={{ color: "#ff8800", mb: 2 }} />
              <Typography sx={{ fontSize: "14px", color: "#64748b" }}>
                <LocationOn sx={{ fontSize: 16, mr: 0.5, verticalAlign: "middle" }} />
                Getting your location...
              </Typography>
            </Box>
          )}

          {/* Location Error */}
          {locationError && (
            <Box sx={{ textAlign: "center", py: 3 }}>
              <Typography sx={{ fontSize: "40px" }}>📍</Typography>
              <Typography sx={{ fontSize: "14px", color: "#dc2626", mt: 1, mb: 2 }}>
                {locationError}
              </Typography>
              <Button
                onClick={getLocation}
                variant="contained"
                startIcon={<Refresh />}
                sx={{
                  bgcolor: "#ff8800",
                  textTransform: "none",
                  "&:hover": { bgcolor: "#e67a00" },
                }}
              >
                Retry Location
              </Button>
            </Box>
          )}

          {/* STEP: Enter Employee ID */}
          {step === "form" && (
            <Box>
              <Typography sx={{ fontSize: "13px", color: "#16a34a", mb: 2, textAlign: "center" }}>
                ✅ Location detected
              </Typography>

              <TextField
                fullWidth
                label="Your Employee ID"
                placeholder="e.g., EMP0001"
                value={employeeId}
                onChange={(e) => setEmployeeId(e.target.value.toUpperCase())}
                autoFocus
                sx={{ mb: 2 }}
                InputProps={{
                  sx: { fontSize: "16px", fontWeight: 600, letterSpacing: "1px" }
                }}
              />

              <Button
                onClick={submitAttendance}
                disabled={loading || !employeeId.trim()}
                fullWidth
                variant="contained"
                sx={{
                  bgcolor: "#ff8800",
                  py: 1.5,
                  fontSize: "15px",
                  fontWeight: 600,
                  textTransform: "none",
                  "&:hover": { bgcolor: "#e67a00" },
                }}
              >
                {loading ? (
                  <CircularProgress size={22} sx={{ color: "#fff" }} />
                ) : (
                  "Mark My Attendance"
                )}
              </Button>
            </Box>
          )}

          {/* STEP: Success */}
          {step === "success" && result && (
            <Box sx={{ textAlign: "center", py: 2 }}>
              <Typography sx={{ fontSize: "56px" }}>
                {result.status === "CHECKED_IN" && "✅"}
                {result.status === "CHECKED_OUT" && "🔴"}
                {result.status === "ALREADY_DONE" && "ℹ️"}
              </Typography>
              <Typography sx={{ fontSize: "18px", fontWeight: 700, color: "#15803d", mt: 1 }}>
                {result.message}
              </Typography>

              <Box sx={{ mt: 3, p: 2, bgcolor: "#f0fdf4", borderRadius: "8px" }}>
                <Typography sx={{ fontSize: "14px", fontWeight: 600 }}>
                  {result.user_name}
                </Typography>
                <Typography sx={{ fontSize: "12px", color: "#64748b", fontFamily: "monospace" }}>
                  {result.unique_id}
                </Typography>

                <Box sx={{ mt: 1.5, display: "flex", justifyContent: "center", gap: 2 }}>
                  {result.in_time && (
                    <Box>
                      <Typography sx={{ fontSize: "10px", color: "#64748b" }}>IN TIME</Typography>
                      <Typography sx={{ fontSize: "14px", fontWeight: 600, color: "#16a34a" }}>
                        {result.in_time}
                      </Typography>
                    </Box>
                  )}
                  {result.out_time && (
                    <Box>
                      <Typography sx={{ fontSize: "10px", color: "#64748b" }}>OUT TIME</Typography>
                      <Typography sx={{ fontSize: "14px", fontWeight: 600, color: "#dc2626" }}>
                        {result.out_time}
                      </Typography>
                    </Box>
                  )}
                </Box>

                {result.total_time && (
                  <Typography sx={{ fontSize: "12px", color: "#64748b", mt: 1 }}>
                    Total: {result.total_time}
                  </Typography>
                )}
              </Box>

              <Button
                onClick={reset}
                variant="outlined"
                sx={{ mt: 2, textTransform: "none" }}
              >
                Mark for Another Employee
              </Button>
            </Box>
          )}

          {/* STEP: Error */}
          {step === "error" && (
            <Box sx={{ textAlign: "center", py: 2 }}>
              <Typography sx={{ fontSize: "56px" }}>❌</Typography>
              <Typography sx={{ fontSize: "16px", color: "#dc2626", fontWeight: 600, mt: 1 }}>
                {error || "Something went wrong"}
              </Typography>
              <Button
                onClick={reset}
                variant="contained"
                sx={{
                  mt: 2,
                  bgcolor: "#ff8800",
                  textTransform: "none",
                  "&:hover": { bgcolor: "#e67a00" },
                }}
              >
                Try Again
              </Button>
            </Box>
          )}

          {/* Footer */}
          <Typography sx={{ fontSize: "10px", color: "#94a3b8", textAlign: "center", mt: 3 }}>
            Powered by Cranoist Attendance System
          </Typography>
        </Paper>
      </Box>
    </>
  );
}

export default MarkAttendanceComponent;